// src/data/products.api.ts
import type { BackendProduct } from './types';
import { mapProductFromBackend, type Product } from './products';
import { env } from '../lib/env';

export type ProductListParams = {
  page?: number;
  limit?: number;
  categorySlug?: string | null;
  categorySlugs?: string[];
  brandSlug?: string | null;
  sourceMarket?: string | null;
  search?: string | null;
  sort?: 'newest' | 'price-low' | 'price-high';
  sizes?: string[];
  onSale?: boolean;
};

export type ProductListResponse = {
  products: Product[];
  pagination: {
    page: number;
    limit: number;
    totalCount?: number;
    totalPages?: number;
    hasNextPage: boolean;
    hasPrevPage: boolean;
  };
  priceBounds: {
    min: number;
    max: number;
  };
};

export type HomeRailProductSource =
  | { type: 'category'; categorySlug: string }
  | { type: 'brand'; brandSlug: string }
  | { type: 'search'; search: string }
  | { type: 'sourceMarket'; sourceMarket: string }
  | { type: 'sale' }
  | { type: 'manual'; productIds: string[] };

export type HomeRailRequest = {
  key: string;
  limit?: number;
  source: HomeRailProductSource;
};

export type HomeRailResponse = {
  rails: Record<string, Product[]>;
};

const toSafeNumber = (value: unknown) => {
  const parsed = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const buildProductListParams = (params: ProductListParams = {}) => {
  const searchParams = new URLSearchParams();

  searchParams.set('page', String(params.page ?? 1));
  searchParams.set('limit', String(params.limit ?? 24));
  searchParams.set('include_total', 'true');

  if (params.categorySlugs?.length) {
    searchParams.set('category_slug', params.categorySlugs.join(','));
  } else if (params.categorySlug) {
    searchParams.set('category_slug', params.categorySlug);
  }
  if (params.brandSlug) searchParams.set('brand_slug', params.brandSlug);
  if (params.sourceMarket) searchParams.set('source_market', params.sourceMarket);
  if (params.search?.trim()) searchParams.set('search', params.search.trim());
  if (params.sort) searchParams.set('sort', params.sort);
  if (params.sizes?.length) searchParams.set('sizes', params.sizes.join(','));
  if (params.onSale) searchParams.set('sale', 'true');

  return searchParams;
};

export async function fetchProducts(params: ProductListParams = {}): Promise<ProductListResponse> {
  const searchParams = buildProductListParams(params);
  const response = await fetch(`${env.API_URL}/api/products?${searchParams.toString()}`);

  if (!response.ok) {
    throw new Error('Failed to fetch products');
  }

  const payload = (await response.json()) as {
    products: BackendProduct[];
    pagination: ProductListResponse['pagination'];
    priceBounds?: ProductListResponse['priceBounds'];
  };

  return {
    products: payload.products.map(mapProductFromBackend),
    pagination: payload.pagination,
    priceBounds: {
      min: toSafeNumber(payload.priceBounds?.min),
      max: toSafeNumber(payload.priceBounds?.max),
    },
  };
}

export async function fetchHomeProductRails(rails: HomeRailRequest[]): Promise<HomeRailResponse> {
  const response = await fetch(`${env.API_URL}/api/products/home-rails`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ rails }),
  });

  if (!response.ok) {
    throw new Error('Failed to fetch homepage product rails');
  }

  const payload = (await response.json()) as {
    rails: Record<string, BackendProduct[]>;
  };

  return {
    rails: Object.fromEntries(
      Object.entries(payload.rails).map(([key, products]) => [
        key,
        products.map(mapProductFromBackend),
      ])
    ),
  };
}

export async function fetchProductBySlug(slug: string): Promise<Product | null> {
  if (!slug) return null;
  const response = await fetch(`${env.API_URL}/api/products/${encodeURIComponent(slug)}`);

  if (response.status === 404) {
    return null;
  }

  if (!response.ok) {
    throw new Error('Failed to fetch product');
  }

  const payload = (await response.json()) as BackendProduct;
  return mapProductFromBackend(payload);
}
